import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OrgUnitDetailView } from '@today/ui-domain'
import { type OrgTypeDTO, type OrgUnitDTO, organizationService } from '@/services/organization.service'
import { type OrgT, useEditorState, viewEditor } from './org-editor'
import { httpStatusOf } from './org-errors'
import {
	buildUnitCreateBody,
	buildUnitPatch,
	EMPTY_UNIT_FORM,
	type UnitFormValues,
	unitFormErrors,
	unitToForm,
} from './unit-form'
import { ORG_UNITS_KEY } from './useOrgTree'

export const orgUnitKey = (id: string | null) => ['org-unit', id] as const

type DetailState = 'none' | 'loading' | 'notFound' | 'error' | 'ready'

const detailState = (
	unitId: string | null,
	q: { isPending: boolean; isError: boolean; error: unknown },
): DetailState => {
	if (!unitId) return 'none'
	if (q.isError) return httpStatusOf(q.error) === 404 ? 'notFound' : 'error'
	return q.isPending ? 'loading' : 'ready'
}

/** `CODE · Name`, or just the code when the name is unknown. */
const withName = (code: string, name: string | undefined) => (name ? `${code} · ${name}` : code)

const dateLabel = (iso: string | null, locale: string) => (iso ? new Date(iso).toLocaleDateString(locale) : null)

/** The detail pane's read fields (REQ-005), with dates in the viewer's locale. */
export const toDetailView = (
	u: OrgUnitDTO,
	types: OrgTypeDTO[],
	parent: OrgUnitDTO | undefined,
	locale: string,
): OrgUnitDetailView => ({
	id: u.id,
	code: u.code,
	name: u.name,
	shortName: u.shortName,
	externalId: u.externalId,
	typeLabel: withName(u.typeCode, types.find((type) => type.code === u.typeCode)?.name),
	parentLabel: u.parentCode ? withName(u.parentCode, parent?.name) : null,
	status: u.status,
	isPhysical: u.isPhysical,
	institutionCode: u.institutionCode,
	countryCode: u.countryCode,
	timezone: u.timezone,
	validFrom: dateLabel(u.validFrom, locale),
	validTo: dateLabel(u.validTo, locale),
	metadata: Object.keys(u.metadata).length === 0 ? null : JSON.stringify(u.metadata, null, 2),
})

type UnitForm = ReturnType<typeof useEditorState<UnitFormValues, { original: OrgUnitDTO | null; parentLabel: string }>>

/** Create or PATCH the dialog's unit; success caches it, selects it and refetches the tree. */
function useUnitSave(form: UnitForm, onSaved: (id: string) => void) {
	const client = useQueryClient()
	return useMutation({
		mutationFn: async ({ values, original }: { values: UnitFormValues; original: OrgUnitDTO | null }) =>
			original
				? organizationService.updateUnit(original.id, buildUnitPatch(original, values))
				: organizationService.createUnit(buildUnitCreateBody(values)),
		onSuccess: async (saved) => {
			client.setQueryData(orgUnitKey(saved.id), saved)
			form.close()
			onSaved(saved.id)
			await client.invalidateQueries({ queryKey: ORG_UNITS_KEY })
		},
		onError: form.refused,
	})
}

interface UnitInput {
	unitId: string | null
	enabled: boolean
	t: OrgT
	onSaved: (id: string) => void
}

/**
 * The selected unit and its create/edit dialog (REQ-005). A save refetches the
 * tree and selects the created/edited unit; refusals map per REQ-010.
 */
export function useOrgUnit({ unitId, enabled, t, onSaved }: UnitInput) {
	const unit = useQuery({
		queryKey: orgUnitKey(unitId),
		queryFn: () => organizationService.getUnit(unitId as string),
		enabled: enabled && unitId !== null,
	})
	const form: UnitForm = useEditorState(t)
	const save = useUnitSave(form, onSaved)
	return {
		state: detailState(unitId, unit),
		unit: unit.data ?? null,
		retry: () => void unit.refetch(),
		editor: viewEditor(form.editor),
		parentLabel: form.editor?.extra.parentLabel ?? '',
		isSaving: save.isPending,
		openCreate: (parent: OrgUnitDTO | null) =>
			form.open(
				'create',
				{ ...EMPTY_UNIT_FORM, parentCode: parent?.code ?? '' },
				{ original: null, parentLabel: parent ? `${parent.code} · ${parent.name}` : t('unit.root') },
			),
		openEdit: (u: OrgUnitDTO) =>
			form.open('edit', unitToForm(u), { original: u, parentLabel: u.parentCode ?? t('unit.root') }),
		change: form.change,
		close: form.close,
		submit: () => {
			if (!form.editor) return
			const errors = unitFormErrors(form.editor.values)
			if (Object.keys(errors).length > 0) return form.invalid(errors)
			save.mutate({ values: form.editor.values, original: form.editor.extra.original })
		},
	}
}
